import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBills, fetchSchemeList } from '../api';
import CreateBillPopup from '../components/CreateBillPopup';
import { usePermissions } from '../../../hooks/usePermissions';
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip, Modal, Row, Col, Table, Input, Button, Space, Typography, Spin, Pagination, Grid } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const BillingList = () => {
  const [bills, setBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [approvalModal, setApprovalModal] = useState({ visible: false, instance: null });
  const navigate = useNavigate();
  const { canViewModule, canViewCreateModule, canViewField } = usePermissions();

  // Field-level view permission checks for list columns
  const canViewTotal = canViewField('billing', 'total');
  const screens = useBreakpoint();
  const [schemesList, setSchemesList] = useState([]);

  useEffect(() => {
    loadSchemes();
    loadBills();
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const loadBills = async () => {
    setLoading(true);
    try {
      const response = await fetchBills({
        search: searchQuery,
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setBills(response.data);
      setPagination((prev) => ({
        ...prev,
        total: response.meta?.total || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch bills', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSchemes = async () => {
    try {
      setLoading(true);
      const response = await fetchSchemeList({});
      console.log(' $$ loadSchemes response: ' + JSON.stringify(response));
      setSchemesList(response.data?.data || []); // <-- FIX: use response.data.data
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch schemes:', error);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchQuery(searchTerm.trim());
  };

  const handleTableChange = (page, pageSize) => {
    setPagination({ ...pagination, current: page, pageSize });
  };

  const handleApprovalInfo = (instance) => {
    setApprovalModal({ visible: true, instance });
  };

  const handleApprovalModalClose = () => {
    setApprovalModal({ visible: false, instance: null });
  };

  const columns = [
    {
      title: 'Bill ID',
      dataIndex: 'billId',
      key: 'billId',
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
    {
      title: 'Customer Name',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
      render: (text) => text || '-',
      responsive: ['sm', 'md', 'lg'],
    },
    {
      title: 'Purchase Date',
      dataIndex: 'purchaseDate',
      key: 'purchaseDate',
      render: (date) => new Date(date).toLocaleDateString(),
      responsive: ['sm', 'md', 'lg'],
    },
    canViewTotal && {
      title: 'Final Amount',
      dataIndex: 'finalAmount',
      key: 'finalAmount',
      render: (amount) => `₹ ${amount.toFixed(2)}`,
      responsive: ['sm', 'md', 'lg'],
    },
    {
      title: 'Approval',
      key: 'approval',
      render: (_, record) => {
        const ai = record.approvalInstance;
        if (!ai) return <span style={{ color: "#aaa" }}>-</span>;
        return (
          <Space>
            <span
              style={{
                fontWeight: 500,
                color:
                  ai.status === "completed"
                    ? "#52c41a"
                    : ai.status === "pending"
                      ? "#faad14"
                      : ai.status === "rejected"
                        ? "#ff4d4f"
                        : "#888",
                textTransform: "capitalize",
              }}
            >
              {ai.status}
            </span>
            <Tooltip title="View approval details">
              <Button
                type="link"
                icon={<InfoCircleOutlined />}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleApprovalInfo(ai);
                }}
                aria-label="View approval details"
              />
            </Tooltip>
          </Space>
        );
      },
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
  ].filter(Boolean);

  if (!canViewModule('billing')) {
    return (
      <div className="text-center mt-5">
        <Text type="danger">You do not have permission to view bills.</Text>
      </div>
    );
  }

  return (
    <div className="container-fluid px-2 px-md-4 py-3">
      <Row justify="space-between" align="middle" className="mb-3">
        <Col xs={24} md={12}>
          <Title level={3} className="mb-0">Billing</Title>
        </Col>
        <Col xs={24} md="auto" className="d-flex justify-content-md-end mt-2 mt-md-0">
          <Space>
            <Input.Search
              placeholder="Search by Bill ID, Phone, Aadhaar"
              allowClear
              enterButton="Search"
              size="middle"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={handleSearch}
              style={{ minWidth: screens.xs ? 180 : 300 }}
              aria-label="Search Bills"
            />
            <CreateBillPopup
              schemesList={schemesList}
            />
          </Space>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={bills}
              pagination={false}
              rowKey={(record) => record._id}
              onRow={(record) => ({
                onClick: () => {
                  navigate('/billing/viewCreatedBill', { state: { bill: { data: record } } });
                },
                style: { cursor: 'pointer' },
              })}
              locale={{ emptyText: 'No bills found.' }}
              scroll={{ x: true }}
              className="bg-white"
              size={screens.xs ? "small" : "middle"}
            />
          </Spin>
          <Pagination
            style={{ marginTop: 24, textAlign: 'right' }}
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            pageSizeOptions={['5', '10', '25']}
            onChange={handleTableChange}
            onShowSizeChange={handleTableChange}
            aria-label="Pagination"
          />
        </>
      )}

      {/* Approval Instance Modal */}
      <Modal
        open={approvalModal.visible}
        onCancel={handleApprovalModalClose}
        footer={null}
        title="Approval Details"
        centered
        bodyStyle={{
          padding: 24,
          minWidth: 320,
        }}
        width={screens.xs ? "95vw" : 480}
      >
        {approvalModal.instance ? (
          <div
            className="d-flex flex-wrap align-items-center justify-content-between gap-3"
            style={{
              background: "#fafcff",
              borderRadius: 8,
              padding: "16px 20px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            }}
          >
            {/* Status Badge */}
            <div>
              <span
                style={{
                  display: "inline-block",
                  fontWeight: 600,
                  fontSize: 14,
                  background: "#f0f5ff",
                  padding: "6px 12px",
                  borderRadius: 20,
                  color:
                    approvalModal.instance.status === "completed"
                      ? "#52c41a"
                      : approvalModal.instance.status === "pending"
                        ? "#faad14"
                        : approvalModal.instance.status === "rejected"
                          ? "#ff4d4f"
                          : "#888",
                  textTransform: "capitalize",
                  letterSpacing: 0.3,
                }}
              >
                Status: {approvalModal.instance.status}
              </span>
            </div>

            {/* Step Info */}
            <div style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>
              <div>Current Step: <strong>{approvalModal.instance.currentStep}</strong></div>
              <div>No. of Steps: <strong>{approvalModal.instance.noOfSteps}</strong></div>
              <div>Pending to: <strong>{approvalModal.instance.pendingAt || "-"}</strong></div>
            </div>
          </div>
        ) : (
          <span style={{ fontSize: 14, color: "#999" }}>
            No approval details found.
          </span>
        )}
      </Modal>
    </div>
  );
};


export default BillingList;
